import imgSelfHavening from "@/assets/somatic/self-havening.jpg";
import imgVooBreath from "@/assets/somatic/voo-breath.jpg";
import imgOrienting from "@/assets/somatic/orienting.jpg";
import imgTreShake from "@/assets/somatic/tre-shake.jpg";
import imgContainer from "@/assets/somatic/container.jpg";
import imgPendulation from "@/assets/somatic/pendulation.jpg";
import imgHandHeart from "@/assets/somatic/hand-on-heart.jpg";
import imgResourcing from "@/assets/somatic/resourcing.jpg";
import imgBoundary from "@/assets/somatic/protective-boundary.jpg";
import imgPMR from "@/assets/somatic/progressive-muscle-relaxation.jpg";
import imgWallPush from "@/assets/somatic/wall-push.jpg";
import imgLegsWall from "@/assets/somatic/legs-up-the-wall.jpg";
import imgButterfly from "@/assets/somatic/butterfly-hug.jpg";
import imgGrounding from "@/assets/somatic/grounding-movement.jpg";
import imgYoga from "@/assets/somatic/trauma-informed-yoga.jpg";

export type Practice = {
  id: string;
  emoji: string;
  title: string;
  duration: string;
  blurb: string;
  what: string;
  why: string;
  steps: string[];
  tips?: string[];
  whenToUse?: string;
  image?: string;
  tier: "guest" | "member";
};

export const PRACTICES: Practice[] = [
  {
    id: "self-havening",
    emoji: "🫂",
    title: "Self-Havening Touch",
    duration: "4 min",
    image: imgSelfHavening,
    blurb: "A gentle self-soothing touch that signals safety to your nervous system.",
    what: "Self-Havening is a comforting touch sequence — arms crossed over the chest, slow strokes down to the elbows — that produces delta-wave calming.",
    why: "The soothing touch and slow rhythm calm the amygdala and lower physiological stress. It can help interrupt spiralling thoughts and soften big feelings.",
    steps: [
      "Sit comfortably. Cross your arms gently over your chest, palms on opposite shoulders.",
      "Slowly stroke down from shoulders to elbows, like soothing a tender friend.",
      "Add a soft sound: hum, sigh, or a quiet ‘mmm’ on each long exhale.",
      "Continue for 1–2 minutes. Notice any softening, warmth, or settling.",
      "Pause. Rest your hands. Notice what changed in your body, without judgment.",
    ],
    tips: ["Go slower than feels natural.", "If touch feels too much, imagine the strokes instead."],
    whenToUse: "When you feel anxious, overwhelmed, or need a quick reset.",
    tier: "guest",
  },
  {
    id: "voo-breath",
    emoji: "💨",
    title: "Voo Breath (Vagal Toning)",
    duration: "5 min",
    image: imgVooBreath,
    blurb: "A low, resonant sound that tones the vagus nerve and softens activation.",
    what: "A slow inhale followed by a low, humming ‘vooooo’ on the exhale — vibrating through the chest and belly.",
    why: "Vibration and long exhales stimulate the vagus nerve, shifting you out of fight-or-flight and into rest-and-digest.",
    steps: [
      "Sit upright. Rest one hand comfortably on your torso.",
      "Inhale slowly through the nose for a count of 4.",
      "On the exhale, make a low, rumbling ‘vooooo’ sound — like a foghorn — until your breath runs out.",
      "Pause. Let the next breath come on its own.",
      "Repeat 5–7 times. Let the vibration settle through your chest and throat.",
    ],
    tips: ["Lower the pitch than feels comfortable.", "Feel the vibration, don’t force volume."],
    whenToUse: "Before a difficult conversation, at the end of a stressful day, or any time your system needs to downshift.",
    tier: "guest",
  },
  {
    id: "orienting",
    emoji: "🌿",
    title: "Orienting to Safety",
    duration: "3 min",
    image: imgOrienting,
    blurb: "A Somatic Experiencing practice to remind the body ‘I am here, and I am safe right now.’",
    what: "Slowly turning the head and eyes to notice your environment — mapping neutral or pleasant details into the nervous system.",
    why: "Orienting activates the ventral vagal system, the branch of the nervous system that supports safety and connection.",
    steps: [
      "Sit or stand. Let your head turn slowly to the right, following with the eyes.",
      "Pause. Let your eyes land on something neutral or pleasant. Stay with it.",
      "Slowly turn to the left. Pause again on something your eyes want to rest on.",
      "Notice your breath, your feet, the temperature of the air.",
      "Repeat 3–4 times. You are mapping safety into your nervous system.",
    ],
    tips: ["Slow the turn — half the speed you think you need.", "Let the eyes lead the head."],
    whenToUse: "When you feel spaced out, disoriented, or on edge in a new environment.",
    tier: "guest",
  },
  {
    id: "tre-shake",
    emoji: "🌀",
    title: "Tension & Trauma Release (Shaking)",
    duration: "8 min",
    image: imgTreShake,
    blurb: "A gentle, body-led shaking practice to discharge held survival energy.",
    what: "Mammals shake off stress after threat. This practice invites your body to do the same — through soft, guided bouncing and shaking.",
    why: "Shaking discharges held activation from the muscles and helps the nervous system return to baseline.",
    steps: [
      "Stand with feet hip-width apart, knees soft.",
      "Begin to bounce gently at the knees. Let your arms hang loose.",
      "Let the shake travel up through your legs, hips, shoulders, jaw.",
      "Don’t perform it — let your body lead. Yawns, sighs, even tears are welcome.",
      "Slow down gradually. Lie down or sit for 3 minutes of stillness afterward.",
    ],
    tips: ["Stop the moment it feels like too much.", "Aftercare stillness is as important as the shake."],
    whenToUse: "After a stressful day, or when you feel wired but unable to rest.",
    tier: "member",
  },
  {
    id: "container",
    emoji: "🫙",
    title: "Building an Inner Container",
    duration: "10 min",
    image: imgContainer,
    blurb: "A guided visualisation to hold overwhelming feelings safely until you’re ready.",
    what: "A mental image of a strong, sealed container that holds what feels too big to carry right now.",
    why: "Containing overwhelm helps prevent flooding and gives you agency to choose when to meet what’s inside.",
    steps: [
      "Close your eyes if it feels safe — otherwise let your gaze rest softly.",
      "Imagine a container — a jar, a vault, a chest — strong enough to hold anything.",
      "Notice its size, colour, texture, and where it lives in your space.",
      "Picture placing what feels too big into the container. Seal it.",
      "Thank yourself for the wisdom of pausing. The container will be there when you return.",
    ],
    tips: ["Your container can change over time.", "You can return and open it with support."],
    whenToUse: "When feelings are too big to process right now, or before sleep.",
    tier: "member",
  },
  {
    id: "pendulation",
    emoji: "🤲",
    title: "Pendulation (Between Activation & Calm)",
    duration: "7 min",
    image: imgPendulation,
    blurb: "Move attention gently between discomfort and ease — building capacity to feel both.",
    what: "A Somatic Experiencing technique that alternates attention between an activated body area and a neutral or resourceful one.",
    why: "Pendulating expands the window of tolerance and teaches the nervous system that difficult sensation is survivable.",
    steps: [
      "Scan your body. Notice one place that feels activated, tight, or uneasy.",
      "Now find another place that feels even slightly more neutral, warm, or okay.",
      "Bring your attention to the neutral place. Stay for 30 seconds.",
      "Gently move your attention back to the activated place. Stay briefly.",
      "Pendulate slowly between them 4–6 times. Notice how both can coexist.",
    ],
    tips: ["Always end on the neutral place.", "Very short touches on the activated place are enough."],
    whenToUse: "When a strong sensation or emotion feels stuck.",
    tier: "member",
  },
  {
    id: "hand-on-heart",
    emoji: "❤️",
    title: "Hand on Heart Regulation",
    duration: "3 min",
    image: imgHandHeart,
    blurb: "A self-soothing touch practice that quietly signals: I am with myself.",
    what: "Placing a hand over the heart and breathing slowly — a simple, portable regulation tool.",
    why: "Touch and slow breath activate the parasympathetic system and release oxytocin.",
    steps: [
      "Place one or both hands gently over your heart. Notice the warmth and weight.",
      "Take a slow breath in, letting your body rise into your palm.",
      "On the exhale, let your shoulders soften. Whisper inwardly: ‘I am here. I am with me.’",
      "Stay for 8–10 breaths, at whatever pace feels right.",
      "Release the hands and notice what is different — even if subtle.",
    ],
    tips: ["You can do this anywhere — no one will know.", "Add a phrase that lands for you."],
    whenToUse: "Any moment of loneliness, anxiety, or self-criticism.",
    tier: "guest",
  },
  {
    id: "resourcing",
    emoji: "🌱",
    title: "Resourcing Practice",
    duration: "6 min",
    image: imgResourcing,
    blurb: "Anchor your nervous system to something that brings even a hint of safety.",
    what: "Bringing to mind a person, place, memory, pet, song, or object that feels supportive — and feeling it in the body.",
    why: "Resourcing strengthens the neural pathways of safety, making them easier to access under stress.",
    steps: [
      "Bring to mind a resource — a person, a pet, a place, a memory, a song, a colour.",
      "Picture it as vividly as you can. Notice what happens in your body as you do.",
      "Where do you feel the ‘okay-ness’ most? Chest? Hands? Belly? Feet?",
      "Stay with that felt sense. Let it expand by even 5%.",
      "Open your eyes when ready. Your resource is always available — you can return any time.",
    ],
    tips: ["Small resources count.", "You can build a whole library of them."],
    whenToUse: "Before something hard, or as daily nervous-system nourishment.",
    tier: "guest",
  },
  {
    id: "protective-boundary",
    emoji: "🛡️",
    title: "Protective Boundary Exercise",
    duration: "5 min",
    image: imgBoundary,
    blurb: "Use your body to feel a tangible sense of personal space and protection.",
    what: "Extending the arms to define the space around you — a physical way to feel your own boundary.",
    why: "For many survivors, boundary is a felt sense the body has forgotten. This practice re-embodies it.",
    steps: [
      "Stand or sit tall. Extend your arms out in front of you, palms facing forward.",
      "Slowly press the air away, as if creating space between you and the world.",
      "Move your arms to each side, then behind, then overhead — defining your space.",
      "Drop your arms and stand inside the bubble you just created. Breathe.",
      "Notice: this space belongs to you. You decide who and what may enter.",
    ],
    tips: ["Add sound if you like — a firm ‘no’ or ‘mine’.", "Feel the effort in your shoulders."],
    whenToUse: "Before or after boundary-testing situations.",
    tier: "member",
  },
  {
    id: "progressive-muscle-relaxation",
    emoji: "🧘",
    title: "Progressive Muscle Relaxation",
    duration: "10 min",
    image: imgPMR,
    blurb: "Gently tense and release muscle groups to discharge tension and return to baseline.",
    what: "A classic body-scan practice: tensing a muscle group for 5 seconds, then releasing fully.",
    why: "The contrast between tension and release teaches the body what deep relaxation feels like.",
    steps: [
      "Lie down or sit comfortably. Take three slow breaths to arrive.",
      "Curl your toes and feet — hold for 5 seconds — then release fully. Notice the contrast.",
      "Move up through calves, thighs, torso, hands, arms, shoulders, jaw — tense and release.",
      "If tensing feels activating, simply imagine the tension and release.",
      "End by lying still for 2 minutes, noticing the weight of your body resting fully.",
    ],
    tips: ["Skip any area that feels tender.", "The release matters more than the tension."],
    whenToUse: "Before sleep, after physical exertion, or when the body feels wound tight.",
    tier: "member",
  },
  {
    id: "wall-push",
    emoji: "🧍",
    title: "Wall Push",
    duration: "3 min",
    image: imgWallPush,
    blurb: "Press into a wall to discharge fight energy safely and feel your own strength.",
    what: "A grounded push against a wall — engaging the whole body without collapse or conflict.",
    why: "Pushing gives fight energy somewhere to go, restoring a felt sense of strength and containment.",
    steps: [
      "Stand an arm’s length from a sturdy wall. Place both palms flat at shoulder height.",
      "Press into the wall with steady, growing effort — as if you could move it.",
      "Feel your legs, back, and arms switch on. Let the effort build for 10–20 seconds.",
      "Release slowly. Step back, shake out the arms, and notice the aliveness in your body.",
      "Repeat 3–5 times. Pause between rounds to feel what’s settling.",
    ],
    tips: ["Feel the ground through your feet.", "Add a slow exhale on the push."],
    whenToUse: "When anger, frustration, or restlessness needs somewhere to go.",
    tier: "guest",
  },
  {
    id: "legs-up-the-wall",
    emoji: "🦵",
    title: "Legs Up The Wall",
    duration: "10 min",
    image: imgLegsWall,
    blurb: "A deeply restorative posture that calms the nervous system and supports rest.",
    what: "Lying on your back with legs resting straight up against a wall — a gentle inversion.",
    why: "The posture slows the heart rate, lowers blood pressure, and cues the body to rest.",
    steps: [
      "Sit sideways next to a wall. Gently swing your legs up as you lower your back to the floor.",
      "Let your legs rest fully against the wall. Soften your arms by your sides, palms up.",
      "Close your eyes if it feels safe. Let your breath move on its own.",
      "Stay for 5–10 minutes. Notice the body softening into support.",
      "To come out, bend the knees, roll to one side, and rest before sitting up.",
    ],
    tips: ["Use a folded blanket under the hips for comfort.", "Skip if you have unmanaged blood pressure issues."],
    whenToUse: "End of day, after long standing or travel, or when you can’t settle to sleep.",
    tier: "guest",
  },
  {
    id: "butterfly-hug",
    emoji: "🤍",
    title: "Butterfly Hug",
    duration: "4 min",
    image: imgButterfly,
    blurb: "A gentle bilateral self-tap (from EMDR) that helps the nervous system settle and integrate.",
    what: "Crossing the arms and slowly alternating soft taps on each shoulder.",
    why: "Bilateral stimulation supports integration and helps the nervous system down-regulate.",
    steps: [
      "Cross your arms over your chest, fingertips resting near your collarbones.",
      "Slowly alternate tapping — left, right, left, right — at a calm, steady pace.",
      "Breathe naturally. Soften your jaw and shoulders.",
      "Continue for 1–2 minutes. Pause and notice. Repeat if helpful.",
      "End by resting both hands on your heart for a few breaths.",
    ],
    tips: ["Slow, even taps — walking pace.", "Notice, don’t analyse."],
    whenToUse: "During activation, after a hard memory, or to settle before sleep.",
    tier: "guest",
  },
  {
    id: "grounding-movement",
    emoji: "🫁",
    title: "Grounding Movement",
    duration: "5 min",
    image: imgGrounding,
    blurb: "Slow, intentional movement to bring you back into your body and the present moment.",
    what: "Gentle swaying and grounding through the feet to reconnect with the body.",
    why: "Slow rhythmic movement supports the ventral vagal system and re-embodies presence.",
    steps: [
      "Stand with feet hip-width apart. Feel the floor through your soles.",
      "Slowly sway side to side, shifting weight from one foot to the other.",
      "Add gentle arm swings, letting them move naturally with the body.",
      "Pause. Press your feet into the floor and lengthen up through the crown.",
      "Take three slow breaths. Notice what’s different in how you feel.",
    ],
    tips: ["Barefoot is lovely if you can.", "Move slower than music."],
    whenToUse: "After long sitting, or when you feel disconnected from your body.",
    tier: "guest",
  },
  {
    id: "trauma-informed-yoga",
    emoji: "🧘",
    title: "Trauma-Informed Yoga",
    duration: "10–25 min",
    image: imgYoga,
    blurb: "A studio of choice-based, gentle yoga practices — chair, restorative, Nidra, and more.",
    what: "A curated library of trauma-informed yoga practices, all optional, gentle, and beginner-friendly.",
    why: "Yoga supports interoception and safe embodiment when offered with choice.",
    steps: [
      "Open the full yoga studio for a curated library of trauma-informed practices.",
      "All practices are choice-based, gentle, and beginner-friendly.",
      "Pause, modify, or stop at any moment — your body leads.",
    ],
    tier: "guest",
  },
];

export function getPractice(id: string): Practice | undefined {
  return PRACTICES.find((p) => p.id === id);
}
